import { i18n } from "../lib/i18n"
import {
  answerKind,
  choicesFor,
  CHIP_LIMIT,
  offersOp,
  opLabel,
  opsFor,
  RATING_VALUES,
  RELATIVE_UNITS,
  type LogicQuestion,
  type Rule,
} from "../lib/display_logic"

const CHEVRON =
  '<svg class="w-3.5 h-3.5 shrink-0 mt-1 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>'
const CLOSE =
  '<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>'
const PLUS =
  '<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>'
const SEARCH =
  '<svg class="w-3.5 h-3.5 shrink-0 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>'
const CHECK =
  '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>'

const DIVIDER = '<div class="divider my-0"></div>'
const SET_OPS = ["any_of", "all_of", "none_of"]
const PRESENCE_OPS = ["answered", "blank"]
// Up to this many comparisons show as a switch; more get a full-width menu.
const SWITCH_LIMIT = 3

const FIELD = "h-11 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-900"
const MENU = "select select-bordered w-full h-11 min-h-11 text-sm font-medium"
const LABEL = "text-[11px] font-semibold uppercase tracking-wider text-slate-500"

function escape(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

// PillTabsComponent's markup, rendered once by the server into a <template>:
// a track and one pill, cloned for every switch in the rule cards.
export interface PillPrototype {
  track: HTMLElement
  pill: HTMLElement
}

interface RulesData {
  pills: PillPrototype
  rules: Rule[]
  questions: LogicQuestion[]
  eligible: LogicQuestion[]
  join: "AND" | "OR"
  openPicker: number | null
}

export function rulesTemplate({ pills, rules, questions, eligible, join, openPicker }: RulesData): string {
  const lead =
    rules.length > 1
      ? `<div class="flex flex-wrap items-center gap-2 text-sm text-slate-700">
          ${escape(i18n.t("display_logic.show_when"))}
          <select class="select select-bordered select-sm h-9 min-h-9 text-sm font-medium" aria-label="${escape(i18n.t("display_logic.join_label"))}"
                  data-focus-key="join" data-action="change->conditional-display#setJoin">
            <option value="AND" ${join === "AND" ? "selected" : ""}>${escape(i18n.t("display_logic.all"))}</option>
            <option value="OR" ${join === "OR" ? "selected" : ""}>${escape(i18n.t("display_logic.any"))}</option>
          </select>
          ${escape(i18n.t("display_logic.of_these_are_true"))}
        </div>`
      : ""

  const cards = rules
    .map((rule, index) => {
      const question = questions.find((q) => q.id === rule.questionId)
      const open = openPicker === index || rule.questionId == null
      return ruleCard(rule, index, question, eligible, open, pills)
    })
    .join(DIVIDER)

  // One divider above, between and below the rules: never two in a row.
  return `<div class="flex flex-col gap-3">
    ${lead}
    <div class="flex flex-col">${DIVIDER}${cards}${DIVIDER}</div>
    <button type="button" data-focus-key="add" data-action="conditional-display#addRule"
            class="self-start inline-flex items-center gap-1.5 h-9 px-1 rounded-lg text-sm font-medium text-primary hover:bg-slate-50">
      ${PLUS}${escape(i18n.t("display_logic.add_rule"))}
    </button>
  </div>`
}

function ruleCard(
  rule: Rule,
  index: number,
  question: LogicQuestion | undefined,
  eligible: LogicQuestion[],
  open: boolean,
  pills: PillPrototype,
): string {
  return `
    <div class="bg-white py-2 flex flex-col gap-2" data-rule-index="${index}">
      <div class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between">
          <span class="${LABEL}">${escape(i18n.t("display_logic.their_answer_to"))}</span>
          <button type="button" data-index="${index}" data-focus-key="${index}:remove"
                  data-action="conditional-display#removeRule"
                  aria-label="${escape(i18n.t("display_logic.remove_rule"))}"
                  class="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            ${CLOSE}
          </button>
        </div>
        ${questionButton(rule, index, question, open)}
        ${open ? questionList(rule, index, question, eligible) : ""}
      </div>
      ${question && !open ? answerControl(rule, index, question, pills) : ""}
    </div>
  `
}

// The picked question in full: its title wraps rather than truncating.
function questionButton(rule: Rule, index: number, question: LogicQuestion | undefined, open: boolean): string {
  let title = i18n.t("display_logic.pick_question")
  let meta = ""
  if (question) {
    title = question.title
    meta = `${question.section_title} · ${i18n.t(`display_logic.types.${question.question_type}`)}`
  } else if (rule.questionId != null) {
    title = i18n.t("display_logic.question_removed")
  }

  return `
    <button type="button" data-index="${index}" data-focus-key="${index}:question"
            data-action="conditional-display#togglePicker" aria-expanded="${open}"
            class="w-full text-left rounded-lg border ${open ? "border-primary ring-2 ring-primary/15" : "border-slate-300 hover:border-slate-400"} bg-white px-3 py-2 flex gap-2.5 items-start">
      <span class="flex-1 min-w-0 flex flex-col gap-0.5">
        ${meta ? `<span class="text-xxs text-slate-500 break-words">${escape(meta)}</span>` : ""}<span class="text-xs font-medium leading-snug break-words ${question ? "text-slate-900" : "text-slate-500"}">${escape(title)}</span>
      </span>
      ${CHEVRON}
    </button>
  `
}

// Earlier questions only, grouped by section, titles in full. Instruction
// blocks show but can't be picked: they have no answer to check.
function questionList(rule: Rule, index: number, question: LogicQuestion | undefined, eligible: LogicQuestion[]): string {
  const choices = question && !eligible.includes(question) ? [...eligible, question] : eligible
  if (choices.length === 0) {
    return `<p class="text-xs text-slate-500 px-1">${escape(i18n.t("display_logic.no_earlier_questions"))}</p>`
  }

  let section: string | null = null
  const rows = choices
    .map((q) => {
      const heading = q.section_title !== section ? `<div class="${LABEL} px-2 pt-2 pb-1" data-section-heading>${escape(q.section_title)}</div>` : ""
      section = q.section_title
      return heading + questionRow(rule, index, q)
    })
    .join("")

  return `
    <div class="rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-question-picker="${index}">
      <label class="flex items-center gap-2 h-10 px-3 border-b border-slate-100">
        ${SEARCH}
        <input type="search" data-index="${index}" data-focus-key="${index}:qsearch"
               data-action="input->conditional-display#filterQuestions keydown->conditional-display#questionKeydown"
               role="combobox" aria-expanded="true" aria-controls="display-logic-questions-${index}" aria-autocomplete="list"
               placeholder="${escape(i18n.t("display_logic.search_questions"))}"
               aria-label="${escape(i18n.t("display_logic.search_questions"))}"
               class="flex-1 min-w-0 p-0 border-0 bg-transparent text-sm outline-none focus:outline-none focus:ring-0 focus:shadow-none">
      </label>
      <div id="display-logic-questions-${index}" role="listbox" class="max-h-72 overflow-y-auto p-1 flex flex-col" data-question-list="${index}">
        ${rows}
        <p class="hidden px-2 py-2 text-xs text-slate-500" data-question-empty>${escape(i18n.t("display_logic.no_question_matches"))}</p>
      </div>
    </div>
  `
}

function questionRow(rule: Rule, index: number, q: LogicQuestion): string {
  const instructions = q.question_type === "instructions"
  const current = q.id === rule.questionId
  const type = instructions ? i18n.t("display_logic.no_answer_to_check") : i18n.t(`display_logic.types.${q.question_type}`)
  return `<button type="button" role="option" id="display-logic-q-${index}-${q.id}" data-index="${index}" data-question-id="${q.id}"
                  data-focus-key="${index}:question:${q.id}" data-question-label="${escape(q.title.toLowerCase())}"
                  data-action="conditional-display#pickQuestion" ${instructions ? "disabled" : ""} aria-pressed="${current}"
                  class="w-full text-left rounded-md px-2 py-2 flex flex-col gap-0.5 ${current ? "bg-primary/10" : "hover:bg-slate-50"} data-[active]:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400">
            <span class="text-xxs ${instructions ? "" : "text-slate-500"}">${escape(type)}</span><span class="text-xs leading-snug break-words ${current ? "font-medium" : ""}">${escape(q.title)}</span>
          </button>`
}

// A switch for a few comparisons, so each reads at full size.
function opSwitch(rule: Rule, index: number, question: LogicQuestion, ops: string[], pills: PillPrototype): string {
  return pillTrack(
    pills,
    i18n.t("display_logic.comparison_label"),
    ops.map((op) => pill(pills, { index, value: op, label: opLabel(question, op), on: op === rule.op, action: "setOpChip", focus: `op:${op}` })),
  )
}

// The component's track, minus its slide animation: a click redraws the card
// before a slide could play.
function pillTrack(pills: PillPrototype, label: string, items: string[]): string {
  const track = pills.track.cloneNode(false) as HTMLElement
  track.removeAttribute("data-controller")
  track.removeAttribute("data-action")
  track.setAttribute("role", "radiogroup")
  track.setAttribute("aria-label", label)
  track.innerHTML = items.join("")
  return track.outerHTML
}

interface PillOptions {
  index: number
  value: string
  label: string
  on: boolean
  action: string
  focus: string
}

function pill(pills: PillPrototype, { index, value, label, on, action, focus }: PillOptions): string {
  const button = pills.pill.cloneNode(false) as HTMLElement
  button.textContent = label
  button.toggleAttribute("data-active", on)
  button.setAttribute("role", "radio")
  button.setAttribute("aria-checked", String(on))
  Object.assign(button.dataset, { index: String(index), value, focusKey: `${index}:${focus}`, action: `conditional-display#${action}` })
  return button.outerHTML
}

function opMenu(rule: Rule, index: number, question: LogicQuestion): string {
  const pick = offersOp(question, rule.op)
    ? ""
    : `<option value="" selected disabled>${escape(i18n.t("display_logic.pick_comparison"))}</option>`
  return `
    <select data-index="${index}" data-focus-key="${index}:op" data-action="change->conditional-display#setOp"
            aria-label="${escape(i18n.t("display_logic.comparison_label"))}" class="${MENU}">
      ${pick}
      ${opsFor(question)
        .map((op) => `<option value="${op}" ${op === rule.op ? "selected" : ""}>${escape(opLabel(question, op))}</option>`)
        .join("")}
    </select>
  `
}

// A rule holding a comparison the type doesn't offer gets the menu, which can
// show "Pick a comparison" until it's replaced.
function comparison(rule: Rule, index: number, question: LogicQuestion, ops: string[], pills: PillPrototype): string {
  return ops.length <= SWITCH_LIMIT && offersOp(question, rule.op)
    ? opSwitch(rule, index, question, ops, pills)
    : opMenu(rule, index, question)
}

// step="any": the default step of 1 would flag 1500.50 invalid and block the
// settings form's auto-save.
function input(index: number, field: string, type: string, value: string, label: string, width: string): string {
  return `<input type="${type}" ${type === "number" ? 'step="any"' : ""} value="${escape(value)}" data-index="${index}" data-field="${field}"
                 data-focus-key="${index}:${field}" data-action="input->conditional-display#setField"
                 aria-label="${escape(label)}" class="${FIELD} ${width}">`
}

function link(index: number, op: string, label: string): string {
  return `<button type="button" data-index="${index}" data-value="${op}" data-focus-key="${index}:op:${op}"
                  data-action="conditional-display#setOpChip"
                  class="self-start text-xs font-medium text-primary hover:underline">${escape(label)}</button>`
}

function answerControl(rule: Rule, index: number, question: LogicQuestion, pills: PillPrototype): string {
  const kind = answerKind(question.question_type)
  const ops = opsFor(question)

  // A comparison this type doesn't offer gets replaced before any answer is
  // asked for.
  if (kind && !offersOp(question, rule.op)) return opMenu(rule, index, question)

  switch (kind) {
    case "chips":
    case "checkbox":
      return question.question_type === "boolean" ? booleanSwitch(rule, index, pills) : choiceControl(rule, index, question, ops, pills)
    case "rating":
      return comparison(rule, index, question, ops, pills) + ratingScale(rule, index)
    case "number":
    case "date":
    case "datetime":
    case "text":
      return comparison(rule, index, question, ops, pills) + valueInputs(rule, index, kind)
    case "file":
    case "signature":
      return opSwitch(rule, index, question, PRESENCE_OPS, pills)
    default:
      return ""
  }
}

// Matching answers is the main path; answered / skipped sits behind a link.
function choiceControl(rule: Rule, index: number, question: LogicQuestion, ops: string[], pills: PillPrototype): string {
  if (PRESENCE_OPS.includes(rule.op)) {
    return opSwitch(rule, index, question, PRESENCE_OPS, pills) + link(index, "any_of", i18n.t("display_logic.match_answers"))
  }
  return (
    comparison(rule, index, question, ops.filter((op) => SET_OPS.includes(op)), pills) +
    (SET_OPS.includes(rule.op) ? choiceList(rule, index, question) : "") +
    link(index, "answered", i18n.t("display_logic.check_answered"))
  )
}

// Yes / No / Skipped: one pick, no comparison to choose.
function booleanSwitch(rule: Rule, index: number, pills: PillPrototype): string {
  const picked = (value: string) => rule.op === "any_of" && rule.values.length === 1 && rule.values[0] === value
  return pillTrack(pills, i18n.t("display_logic.answer_label"), [
    pill(pills, { index, value: "true", label: i18n.t("display_logic.yes"), on: picked("true"), action: "setAnswer", focus: "chip:true" }),
    pill(pills, { index, value: "false", label: i18n.t("display_logic.no"), on: picked("false"), action: "setAnswer", focus: "chip:false" }),
    pill(pills, { index, value: "blank", label: i18n.t("display_logic.skipped"), on: rule.op === "blank", action: "setOpChip", focus: "op:blank" }),
  ])
}

// 1–10, shading the values an "at least" / "at most" rule takes in.
function ratingScale(rule: Rule, index: number): string {
  const picked = Number(rule.value)
  const inRange = (value: number) =>
    picked > 0 && ((rule.op === "at_least" && value > picked) || (rule.op === "at_most" && value < picked))
  return `<div role="group" aria-label="${escape(i18n.t("display_logic.rating_label"))}" class="grid grid-cols-5 gap-1 p-0.5 rounded-lg bg-slate-100">
    ${RATING_VALUES.map((value) => {
      const on = rule.value === value
      const tone = on ? "bg-primary text-white font-semibold" : inRange(Number(value)) ? "bg-primary/15 text-slate-800" : "text-slate-600 hover:bg-white"
      return `<button type="button" aria-pressed="${on}" data-index="${index}" data-value="${value}"
                      data-focus-key="${index}:rating:${value}" data-action="conditional-display#setRating"
                      class="h-10 rounded-md text-sm ${tone}">${value}</button>`
    }).join("")}
  </div>`
}

function valueInputs(rule: Rule, index: number, kind: "number" | "date" | "datetime" | "text"): string {
  if (PRESENCE_OPS.includes(rule.op) || !rule.op) return ""

  const type = kind === "text" ? "text" : kind === "number" ? "number" : "date"

  // Dates stack: two date inputs side by side clip their text in the rail.
  if (rule.op === "between") {
    const stacked = type === "date"
    const width = stacked ? "w-full" : "flex-1 min-w-0"
    return `<div class="${stacked ? "flex flex-col gap-1.5" : "flex items-center gap-2"} text-sm text-slate-600">
              ${input(index, "value", type, rule.value, i18n.t("display_logic.from_label"), width)}
              ${escape(i18n.t("display_logic.and"))}
              ${input(index, "value2", type, rule.value2, i18n.t("display_logic.to_label"), width)}
            </div>`
  }

  if (rule.op === "more_than_ago" || rule.op === "within_last") {
    const units = RELATIVE_UNITS.map(
      (unit) => `<option value="${unit}" ${unit === rule.unit ? "selected" : ""}>${escape(i18n.t(`display_logic.units.${unit}`))}</option>`,
    ).join("")
    return `<div class="flex items-center gap-2 text-sm text-slate-600">
            ${input(index, "amount", "number", rule.amount, i18n.t("display_logic.amount_label"), "w-20")}
            <select data-index="${index}" data-field="unit" data-focus-key="${index}:unit"
                    data-action="change->conditional-display#setField"
                    aria-label="${escape(i18n.t("display_logic.unit_label"))}" class="${MENU} flex-1 w-auto min-w-0">${units}</select>
            ${rule.op === "more_than_ago" ? escape(i18n.t("display_logic.ago")) : ""}
            </div>`
  }

  return input(index, "value", type, rule.value, i18n.t("display_logic.value_label"), "w-full")
}

// Full-width rows that wrap long answers; a search box past CHIP_LIMIT.
function choiceList(rule: Rule, index: number, question: LogicQuestion): string {
  const choices = choicesFor(question)

  if (choices.length === 0) {
    return `<p class="text-xs text-slate-500">${escape(i18n.t("display_logic.no_answers_yet"))}</p>`
  }

  const rows = choices.map((choice) => choiceRow(index, String(choice.id), choice.label, rule.values.includes(String(choice.id)))).join("")

  if (choices.length <= CHIP_LIMIT) return `<div class="flex flex-col gap-1.5">${rows}</div>`

  return `
    <div class="flex flex-col gap-1.5">
      <label class="flex items-center gap-2 h-11 px-3 rounded-lg border border-slate-300">
        ${SEARCH}
        <input type="search" data-index="${index}" data-focus-key="${index}:search"
               data-action="input->conditional-display#filterChoices"
               placeholder="${escape(i18n.t("display_logic.search_answers", { count: choices.length }))}"
               aria-label="${escape(i18n.t("display_logic.search_label"))}"
               class="flex-1 min-w-0 p-0 border-0 bg-transparent text-sm outline-none focus:outline-none focus:ring-0 focus:shadow-none">
      </label>
      <div class="max-h-72 overflow-y-auto flex flex-col gap-1.5" data-choice-list="${index}">
        ${rows}
        <p class="hidden px-1 py-1.5 text-xs text-slate-500" data-choice-empty>${escape(i18n.t("display_logic.no_matches"))}</p>
      </div>
    </div>
  `
}

function choiceRow(index: number, id: string, label: string, on: boolean): string {
  return `<button type="button" aria-pressed="${on}" data-index="${index}" data-value="${escape(id)}"
                  data-focus-key="${index}:chip:${escape(id)}" data-action="conditional-display#toggleValue"
                  data-choice-label="${escape(label.toLowerCase())}"
                  class="w-full min-h-8 px-3 py-2 rounded-lg border text-left text-sm leading-snug flex items-center gap-2.5 ${on ? "border-primary bg-primary/10" : "border-slate-200 bg-white hover:border-slate-300"}">
            <span class="w-[18px] h-[18px] shrink-0 rounded-[5px] border-[1.5px] flex items-center justify-center ${on ? "border-primary bg-primary text-white" : "border-slate-400 bg-white"}">${on ? CHECK : ""}</span>
            <span class="min-w-0 break-words">${escape(label)}</span>
          </button>`
}
